import { db } from '@/data/firebase/config';
import {
  DocumentSnapshot,
  collection,
  getDocs,
  limit,
  orderBy,
  query,
  startAfter,
} from 'firebase/firestore';
import { useCallback, useEffect, useRef, useState } from 'react';

interface Options {
  pageSize?: number;
}

const usePaginatedDocuments = <T extends { id?: string }>(
  collectionName: string,
  options: Options = {},
) => {
  const { pageSize = 20 } = options;
  const [data, setData] = useState<T[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const lastDocRef = useRef<DocumentSnapshot | null>(null);

  const fetchInitial = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const q = query(
        collection(db, collectionName),
        orderBy('createdAt', 'desc'),
        limit(pageSize),
      );
      const snapshot = await getDocs(q);
      const docs = snapshot.docs.map(
        (doc) => ({ id: doc.id, ...doc.data() } as T),
      );
      lastDocRef.current = snapshot.docs[snapshot.docs.length - 1] ?? null;
      setData(docs);
      setHasMore(snapshot.docs.length === pageSize);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar datos');
    } finally {
      setIsLoading(false);
    }
  }, [collectionName, pageSize]);

  const loadMore = useCallback(async () => {
    if (!lastDocRef.current || isLoadingMore) return;
    setIsLoadingMore(true);
    try {
      const q = query(
        collection(db, collectionName),
        orderBy('createdAt', 'desc'),
        startAfter(lastDocRef.current),
        limit(pageSize),
      );
      const snapshot = await getDocs(q);
      const newDocs = snapshot.docs.map(
        (doc) => ({ id: doc.id, ...doc.data() } as T),
      );
      lastDocRef.current = snapshot.docs[snapshot.docs.length - 1] ?? null;
      setData((prev) => [...prev, ...newDocs]);
      setHasMore(snapshot.docs.length === pageSize);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Error al cargar más');
    } finally {
      setIsLoadingMore(false);
    }
  // isLoadingMore is read inside the guard but must not be in deps — including
  // it recreates the callback on every loading state flip (stale closure).
  // The ref-based guard already prevents concurrent invocations.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [collectionName, pageSize]);

  useEffect(() => {
    fetchInitial();
  }, [fetchInitial]);

  return { data, isLoading, isLoadingMore, hasMore, error, loadMore };
};

export default usePaginatedDocuments;
